// src/app/redux/sagas/avanceSaga.js
import { call, put } from "redux-saga/effects";
import Swal from "sweetalert2";
import { API_BASE_URL, getRequest, postRequest, putRequest, deleteRequest } from "../../../api";

import {
    setAvanceLoading,
    fetchAvanceSuccess,
    fetchAvanceFail,
    fetchAvanceByIdSuccess,
    fetchAvanceByIdFail,
    createAvanceSuccess,
    createAvanceFail,
    updateAvanceSuccess,
    updateAvanceFail,
    deleteAvanceSuccess,
    deleteAvanceFail,
    fetchAvanceActifsSuccess,
    fetchAvanceActifsFail,
} from "../actions/avanceActions";

const extractErrorMessage = (error, fallback) => {
    let msg = fallback;

    if (error?.response?.data?.message) msg = error.response.data.message;
    else if (error?.response?.data?.error) msg = error.response.data.error;
    else if (error?.message) msg = error.message;

    return msg;
};

// ===================== FETCH ALL =====================
export function* fetchAvanceSaga() {
    const token = sessionStorage.getItem('token');
    const url = `${API_BASE_URL}/api/avances`;

    yield put(setAvanceLoading(true));

    try {
        console.group("🔵 FETCH_AVANCE_REQUEST");
        console.log("🌐 URL:", url);
        console.groupEnd();

        const resp = yield call(getRequest, { url, token });

        console.group("✅ FETCH_AVANCE_SUCCESS");
        console.log("📥 Avances:", resp.data.length);
        console.groupEnd();

        yield put(fetchAvanceSuccess(resp.data || []));
        yield put(setAvanceLoading(false));
    } catch (error) {
        console.group("❌ FETCH_AVANCE_FAIL");
        console.error("🚨 Erreur:", error.message);
        console.groupEnd();

        yield put(fetchAvanceFail(error.message));
        yield put(setAvanceLoading(false));

        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "",
            text: "Erreur chargement des avances",
            timer: 2000,
            showConfirmButton: false,
        });
    }
}

// ===================== FETCH BY ID =====================
export function* fetchAvanceByIdSaga(action) {
    const token = sessionStorage.getItem('token');
    const id = action.payload;
    const url = `${API_BASE_URL}/api/avances/${id}`;

    yield put(setAvanceLoading(true));

    try {
        console.group("🔵 FETCH_AVANCE_BY_ID_REQUEST");
        console.log("🆔 ID:", id);
        console.groupEnd();

        const resp = yield call(getRequest, { url, token });

        console.group("✅ FETCH_AVANCE_BY_ID_SUCCESS");
        console.log("📥 Avance:", resp.data);
        console.groupEnd();

        yield put(fetchAvanceByIdSuccess(resp.data));
        yield put(setAvanceLoading(false));
    } catch (error) {
        console.group("❌ FETCH_AVANCE_BY_ID_FAIL");
        console.error("🚨 Erreur:", error.message);
        console.groupEnd();

        yield put(fetchAvanceByIdFail(error.message));
        yield put(setAvanceLoading(false));

        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "",
            text: "Avance introuvable",
            timer: 2000,
            showConfirmButton: false,
        });
    }
}

// ===================== CREATE =====================
export function* createAvanceSaga(action) {
    const token = sessionStorage.getItem('token');
    const url = `${API_BASE_URL}/api/avances`;
    const data = action.payload;

    yield put(setAvanceLoading(true));

    try {
        console.group("🔵 CREATE_AVANCE_REQUEST");
        console.log("📊 Données:", data);
        console.groupEnd();

        const resp = yield call(postRequest, { url, data, token });

        console.group("✅ CREATE_AVANCE_SUCCESS");
        console.log("📥 Avance créée:", resp.data);
        console.groupEnd();

        yield put(createAvanceSuccess(resp.data));
        yield put(setAvanceLoading(false));

        yield call([Swal, Swal.fire], {
            icon: "success",
            title: "",
            text: "Produit ajouté",
            timer: 1600,
            showConfirmButton: false,
        });
    } catch (error) {
        console.group("❌ CREATE_AVANCE_FAIL");
        console.error("🚨 Erreur:", error.message);
        console.groupEnd();

        const errorMessage = extractErrorMessage(error, "Erreur lors de l'ajout");
        yield put(createAvanceFail(errorMessage));
        yield put(setAvanceLoading(false));

        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "Erreur",
            text: errorMessage,
            confirmButtonText: "OK",
            confirmButtonColor: "#d33",
        });
    }
}

// ===================== UPDATE =====================
export function* updateAvanceSaga(action) {
    const token = sessionStorage.getItem("token");
    const { id, payload } = action.payload;
    const data = action.payload.payload;

    const url = `${API_BASE_URL}/api/avances/${id}`;
    yield put(setAvanceLoading(true));

    try {
        console.group("🔵 UPDATE_AVANCE_REQUEST");
        console.log("🆔 ID:", id);
        console.log("📦 Payload:", payload);
        console.groupEnd();

        const resp = yield call(putRequest, { url, data, token });

        console.group("✅ UPDATE_AVANCE_SUCCESS");
        console.log("✅ Updated:", resp.data);
        console.groupEnd();

        yield put(updateAvanceSuccess(resp.data));
        yield put(setAvanceLoading(false));

        yield call([Swal, Swal.fire], {
            icon: "success",
            title: "Succès",
            text: "Modifications mises à jour",
            timer: 1400,
            showConfirmButton: false,
        });
    } catch (error) {
        console.group("❌ UPDATE_AVANCE_FAIL");
        console.error("🚨 Erreur complète:", error);
        console.error("🚨 Response data:", error.response?.data);
        console.error("🚨 Response status:", error.response?.status);
        console.groupEnd();

        const errorMessage = extractErrorMessage(error, "Erreur lors de la mise à jour");
        yield put(updateAvanceFail(errorMessage));
        yield put(setAvanceLoading(false));

        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "Erreur",
            text: errorMessage,
            confirmButtonText: "OK",
            confirmButtonColor: "#d33",
        });
    }
}

// ===================== DELETE =====================
export function* deleteAvanceSaga(action) {
    const token = sessionStorage.getItem('token');
    const id = action.payload;
    const url = `${API_BASE_URL}/api/avances/${id}`;

    yield put(setAvanceLoading(true));

    try {
        console.group("🔵 DELETE_AVANCE_REQUEST");
        console.log("🆔 ID:", id);
        console.groupEnd();

        yield call(deleteRequest, { url, token });

        console.group("✅ DELETE_AVANCE_SUCCESS");
        console.log("🗑️ Supprimé:", id);
        console.groupEnd();

        yield put(deleteAvanceSuccess(id));
        yield put(setAvanceLoading(false));

        yield call([Swal, Swal.fire], {
            icon: "success",
            title: "",
            text: "Produit supprimé",
            timer: 1400,
            showConfirmButton: false,
        });
    } catch (error) {
        console.group("❌ DELETE_AVANCE_FAIL");
        console.error("🚨 Erreur complète:", error);
        console.error("🚨 Response data:", error.response?.data);
        console.error("🚨 Response status:", error.response?.status);
        console.groupEnd();

        yield put(setAvanceLoading(false));

        let errorMessage = "Erreur lors de la suppression";

        if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
        } else if (error.response?.data?.error) {
            errorMessage = error.response.data.error;
        } else if (error.message) {
            errorMessage = error.message;
        }

        yield put(deleteAvanceFail(errorMessage));

        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "Erreur de suppression",
            text: errorMessage,
            confirmButtonText: "OK",
            confirmButtonColor: "#d33",
        });
    }
}

// ===================== FETCH ACTIFS =====================
export function* fetchAvanceActifsSaga() {
    const token = sessionStorage.getItem('token');
    const url = `${API_BASE_URL}/api/avances/actifs`;

    yield put(setAvanceLoading(true));

    try {
        console.group("🔵 FETCH_AVANCE_ACTIFS_REQUEST");
        console.log("🌐 URL:", url);
        console.groupEnd();

        const resp = yield call(getRequest, { url, token });

        console.group("✅ FETCH_AVANCE_ACTIFS_SUCCESS");
        console.log("📥 Avances actives:", resp.data.length);
        console.groupEnd();

        yield put(fetchAvanceActifsSuccess(resp.data || []));
        yield put(setAvanceLoading(false));
    } catch (error) {
        console.group("❌ FETCH_AVANCE_ACTIFS_FAIL");
        console.error("🚨 Erreur:", error.message);
        console.groupEnd();

        yield put(fetchAvanceActifsFail(error.message));
        yield put(setAvanceLoading(false));

        yield call([Swal, Swal.fire], {
            icon: "error",
            title: "",
            text: "Erreur chargement avances actives",
            timer: 2000,
            showConfirmButton: false,
        });
    }
}